/**
 * 角色列表（權限管理）。
 *
 * 切片 1：唯讀列表。切片 4：新增自訂角色（inline 表單）。
 * 對應 BE GET /admin/roles、POST /admin/roles。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '../components/Button';
import { Pill } from '../components/Pill';
import { createRole, listRoles, type RbacRoleListItem } from '../lib/api-client';

const KEY_RE = /^[a-z][a-z0-9_]{2,49}$/;

export function RolesListPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    key: '',
    display_name: '',
    description: '',
    require_2fa: false,
  });

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin.roles'],
    queryFn: listRoles,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createRole({
        key: form.key.trim(),
        display_name: form.display_name.trim(),
        description: form.description.trim() || undefined,
        require_2fa: form.require_2fa,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin.roles'] });
      setCreating(false);
      setForm({ key: '', display_name: '', description: '', require_2fa: false });
    },
  });

  const items: RbacRoleListItem[] = data?.items ?? [];
  const keyValid = KEY_RE.test(form.key.trim());
  const canCreate = keyValid && form.display_name.trim().length > 0 && !createMutation.isPending;

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">角色管理</h1>
        <div className="page-head__actions">
          <Button
            variant="primary"
            onClick={() => {
              createMutation.reset();
              setCreating((v) => !v);
            }}
          >
            {creating ? '收合新增表單' : '+ 新增角色'}
          </Button>
          <Button variant="secondary" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? '更新中…' : '重新整理'}
          </Button>
        </div>
      </div>

      <p className="page-intro">
        系統角色清單。系統角色（seed）權限可調但不可刪除；自訂角色可編輯與刪除（無使用者掛載時）。
        點選一列檢視 / 編輯角色權限。
      </p>

      {creating && (
        <div className="card" style={{ marginBottom: 16, maxWidth: 560 }}>
          <h2 style={{ fontSize: 15, margin: '0 0 10px 0' }}>新增自訂角色</h2>
          <div style={{ display: 'grid', gap: 8 }}>
            <label style={{ fontSize: 13 }}>
              角色 key（小寫英數底線、3~50 字；建立後不可改）
              <input
                type="text"
                value={form.key}
                onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))}
                placeholder="例：ops_supervisor"
                style={{ width: '100%', padding: 6, fontFamily: 'monospace' }}
              />
              {form.key.trim().length > 0 && !keyValid && (
                <span style={{ color: 'var(--c-danger, #b91c1c)', fontSize: 12 }}>
                  格式不符（須小寫英文起首之英數底線、3~50 字）
                </span>
              )}
            </label>
            <label style={{ fontSize: 13 }}>
              顯示名稱
              <input
                type="text"
                value={form.display_name}
                onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
                style={{ width: '100%', padding: 6 }}
              />
            </label>
            <label style={{ fontSize: 13 }}>
              說明（可選）
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                style={{ width: '100%', padding: 6 }}
              />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={form.require_2fa}
                onChange={(e) => setForm((f) => ({ ...f, require_2fa: e.target.checked }))}
              />
              強制 2FA（持此角色之使用者登入須通過 TOTP）
            </label>
            {createMutation.error && (
              <div className="error-banner">
                建立失敗：{(createMutation.error as Error).message}
              </div>
            )}
            <div>
              <Button
                variant="primary"
                onClick={() => createMutation.mutate()}
                disabled={!canCreate}
              >
                {createMutation.isPending ? '建立中…' : '建立角色'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {isLoading && <div>載入中…</div>}
      {error && (
        <div className="error-banner">
          {(error as Error).message}
          <div style={{ marginTop: 4, fontSize: 12 }}>
            提醒：此頁需 superadmin / ticketing_admin token（admin.audit_log 權限）。
          </div>
        </div>
      )}

      {data && (
        <>
          <div className="table-card">
            <table>
              <thead>
                <tr>
                  <th>角色 key</th>
                  <th>顯示名稱</th>
                  <th>類型</th>
                  <th>2FA</th>
                  <th style={{ textAlign: 'right' }}>權限數</th>
                  <th style={{ textAlign: 'right' }}>使用者數</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="empty-cell">
                      目前無角色
                    </td>
                  </tr>
                )}
                {items.map((r) => (
                  <tr key={r.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      <Link to={`/admin/roles/${r.id}`}>{r.key}</Link>
                    </td>
                    <td>{r.display_name}</td>
                    <td>
                      {r.is_system ? (
                        <Pill tone="brand">系統角色</Pill>
                      ) : (
                        <Pill tone="neutral">自訂角色</Pill>
                      )}
                    </td>
                    <td>
                      {r.require_2fa ? (
                        <Pill tone="warning">強制 2FA</Pill>
                      ) : (
                        <Pill tone="neutral">免</Pill>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>{r.permission_count}</td>
                    <td style={{ textAlign: 'right' }}>{r.user_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="list-foot">
            <div>共 {data.total} 個角色</div>
          </div>
        </>
      )}
    </div>
  );
}
